'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * Dunne voortgangsbalk bovenaan het scherm bij paginanavigatie.
 *
 * App Router-navigaties kunnen seconden duren (server components laden) zonder
 * enige visuele feedback; bezoekers klikten daardoor nogmaals of dachten dat de
 * site hing. De balk start bij een klik op een interne link (capture-fase) en
 * dooft zodra de nieuwe route gerenderd is (pathname/searchParams wijzigen).
 */

function isInternalNavigation(anchor: HTMLAnchorElement, event: MouseEvent) {
  if (event.defaultPrevented) return false;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;
  const href = anchor.getAttribute('href') || '';
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false;
  let url: URL;
  try {
    url = new URL(anchor.href, window.location.href);
  } catch {
    return false;
  }
  if (url.origin !== window.location.origin) return false;
  // Zelfde pagina (alleen een hash of exact dezelfde URL): niets te laden.
  const current = window.location.pathname + window.location.search;
  const next = url.pathname + url.search;
  if (next === current) return false;
  return true;
}

function ProgressBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [state, setState] = useState<'idle' | 'loading' | 'finishing'>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const start = () => {
      if (stateRef.current === 'loading') return;
      if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
      setState('loading');
      // Vangnet: als de navigatie nooit landt (extern gedrag, download,
      // afgebroken), laat de balk niet eeuwig hangen.
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setState('idle'), 15000);
    };

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const anchor = target?.closest?.('a');
      if (anchor instanceof HTMLAnchorElement && isInternalNavigation(anchor, event)) start();
    };
    const onPopState = () => start();

    document.addEventListener('click', onClick, { capture: true });
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  // Route gewijzigd: navigatie is geland, balk afronden.
  useEffect(() => {
    if (stateRef.current !== 'loading') return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setState('finishing');
    finishTimerRef.current = setTimeout(() => setState('idle'), 350);
    return () => {
      if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
    };
  }, [pathname, searchParams]);

  if (state === 'idle') return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]" aria-hidden="true">
      <style>{`
        @keyframes printen-nav-progress {
          0% { width: 0%; }
          15% { width: 40%; }
          55% { width: 68%; }
          100% { width: 84%; }
        }
      `}</style>
      <div
        className="h-full rounded-r-full bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 shadow-[0_0_8px_rgba(225,29,72,0.6)]"
        style={
          state === 'loading'
            ? { animation: 'printen-nav-progress 8s ease-out forwards' }
            : { width: '100%', opacity: 0, transition: 'width 200ms ease-in, opacity 300ms ease-in 100ms' }
        }
      />
    </div>
  );
}

export function NavigationProgress() {
  return (
    <Suspense fallback={null}>
      <ProgressBar />
    </Suspense>
  );
}
